import { ReparoApi } from './index';

export type CatalogRowType = 'product' | 'maker' | 'bom';

/** How much of the file an import reads and a template writes. Chosen once; both follow it. */
export type CatalogScope = 'products' | 'makers' | 'all';

export const CATALOG_SCOPE_OPTIONS: Array<{ value: CatalogScope; label: string; description: string }> = [
    { value: 'products', label: 'Products only', description: 'Product rows. Maker and recipe rows in the file are listed and left alone.' },
    { value: 'makers', label: 'Products and makers', description: 'Also adds makers and their dated prices. Recipe rows are listed and left alone.' },
    { value: 'all', label: 'Products, makers and recipes', description: 'Everything in the file.' },
];

/** What the catalogue importer reports: one section per row type, plus the generic totals the import modal reads. */
export type CatalogImportResult = {
    totalRows: number;
    dryRun: boolean;
    products: { rows: number; created: number; updated: number; skipped: number };
    makers: { rows: number; created: number; updated: number; skipped: number };
    bom: { rows: number; written: number };
    errors: Array<{ row: number; type: CatalogRowType; key: string; reason: string }>;
    created: number;
    updated: number;
    skipped: number;
};

/**
 * The catalogue file's columns, in template order. One header serves every row type: a
 * product row fills the product columns, a maker row `vendor_id`, `currency`, `moq`,
 * `price` and `effective_from`, a bom row `material_id`, `quantity` and `recipe_vendor_id`.
 */
export const CATALOG_CSV_COLUMNS = [
    'row_type',
    'product_id',
    'name',
    'sku',
    'category',
    'sub_category',
    'product_group',
    'product_type',
    'price',
    'weight_grams',
    'volume_cbm',
    'gst_rate',
    'hsn_code',
    'units_per_case',
    'ean',
    'is_active',
    'vendor_id',
    'currency',
    'moq',
    'effective_from',
    'material_id',
    'quantity',
    'recipe_vendor_id',
] as const;

export const CATALOG_REQUIRED_COLUMNS = ['product_id'] as const;

const MAKER_COLUMNS = new Set(['vendor_id', 'currency', 'moq', 'effective_from']);
const BOM_COLUMNS = new Set(['material_id', 'quantity', 'recipe_vendor_id']);

/**
 * The file's columns at a scope: the full list less the row types it leaves out. Products
 * only also drops `row_type`, so that file is the plain products file the grid downloads.
 * Mirrors the server's own derivation, which is what writes the template.
 */
export const catalogColumnsFor = (scope: CatalogScope): string[] =>
    CATALOG_CSV_COLUMNS.filter((c) => {
        if (c === 'row_type') return scope !== 'products';
        if (MAKER_COLUMNS.has(c)) return scope !== 'products';
        if (BOM_COLUMNS.has(c)) return scope === 'all';
        return true;
    });

/**
 * ADMIN only. Products, makers and recipe lines from one CSV or workbook, read at the chosen
 * scope: rows outside it are listed in the result and never applied. A file with no
 * `row_type` column is read as products only. `dryRun` checks everything and writes nothing.
 * An import never removes anything.
 */
export const importCatalogFile = async (file: File, dryRun: boolean, scope: CatalogScope): Promise<CatalogImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams({ dryRun: String(dryRun), scope });
    return ReparoApi<any>({
        method: 'POST',
        url: `/catalog/upsert?${params.toString()}`,
        body: formData,
    });
};

/** The catalogue at a scope, in the import's own shape: each product, then its makers and recipe lines if the scope has them. */
export const getCatalogTemplate = async (
    scope: CatalogScope,
    productIds?: string[],
): Promise<{ columns: readonly string[]; rows: Record<string, string>[] }> =>
    ReparoApi<any>({
        method: 'POST',
        url: '/catalog/template',
        body: JSON.stringify({ scope, ...(productIds?.length ? { productIds } : {}) }),
    });
